import { useState } from 'react'
import { Card, Col, Row, Select, Space, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { usePermissions } from '@/shared/auth/usePermissions'
import { ServerExportButton } from '@/shared/ui/ServerExportButton'
import {
  EXPENSE_GROUPINGS,
  FINANCE_EXPORT_KINDS,
  downloadFinanceExport,
  type ExpenseGrouping,
  type FinanceExportKind,
  type Period,
} from './financeApi'

export interface FinanceExportCardProps {
  periods: readonly Period[]
}

/**
 * Moliyaviy hisobotni fayl sifatida yuklab olish.
 *
 * <h2>⚠️ Nima yopiq edi</h2>
 * `GET /api/finance/reports/export/{kind}` backendda BOR va beshta
 * hisobotni beradi (aylanma qaydnoma, foyda va zarar, xarajatlar,
 * xarajat markazlari, filiallar). Panel unga UMUMAN murojaat qilmasdi —
 * ya'ni buxgalter faylni faqat qo'lda `curl` bilan ola olardi.
 *
 * <h2>⚠️ Nega aynan DAVRLAR sahifasida</h2>
 * Endpoint `from`/`to` kunlarini oladi va moliyaviy hisobot HAR DOIM
 * davr chegarasida o'qiladi. Davr ro'yxati aynan shu sahifada, ya'ni
 * operator sanani qo'lda terib xato qilmaydi: u tayyor davrni tanlaydi.
 *
 * ⚠️ Davr oxiri (`endsOn`) CHEGARADAN TASHQARI saqlanadi (avgust uchun
 * `2026-09-01`), endpoint esa ikkala chegarani ham KIRITADI. Bir kun
 * ayirilmasa fayl keyingi oyning birinchi kunini ham qamrab olardi va
 * ikki davr yig'indisi kompaniya jamisidan katta chiqardi.
 *
 * <h2>⚠️ Ikki huquq birga</h2>
 * `@RequiresPermission({FINANCE_ACCOUNT_VIEW, REPORT_EXPORT})` AND bilan
 * ishlaydi. Kartochka ikkalasi ham bo'lgandagina chiziladi — aks holda
 * operator tugmani ko'rib turib 403 olardi.
 */
export function FinanceExportCard({ periods }: FinanceExportCardProps) {
  const { t } = useTranslation()
  const { can } = usePermissions()

  const [kind, setKind] = useState<FinanceExportKind>('trial-balance')
  const [periodId, setPeriodId] = useState<string | null>(null)
  const [groupBy, setGroupBy] = useState<ExpenseGrouping>('ACCOUNT')

  if (!can('finance.account.view') || !can('report.export')) return null

  const period = periods.find((p) => p.id === periodId) ?? null
  const from = period?.startsOn
  // ⚠️ `endsOn` exclusive — bir kun ayiriladi (yuqoridagi javadoc).
  const to = period ? isoMinusOneDay(period.endsOn) : undefined

  return (
    <Card size="small" title={t('export.finance.title')} style={{ marginBottom: 16 }}>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        {t('export.finance.subtitle')}
      </Typography.Paragraph>

      <Row gutter={[16, 16]} align="bottom">
        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('export.finance.kind')}
          </Typography.Text>
          <Select<FinanceExportKind>
            value={kind}
            onChange={setKind}
            style={{ width: '100%' }}
            options={FINANCE_EXPORT_KINDS.map((value) => ({
              value,
              label: t(`export.finance.kinds.${value}`),
            }))}
          />
        </Col>

        <Col xs={24} md={8}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('export.finance.period')}
          </Typography.Text>
          <Select<string>
            allowClear
            value={periodId}
            onChange={(value) => setPeriodId(value ?? null)}
            placeholder={t('export.finance.periodAll')}
            style={{ width: '100%' }}
            options={periods.map((p) => ({ value: p.id, label: p.code }))}
          />
        </Col>

        {kind === 'expenses' ? (
          <Col xs={24} md={8}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('export.finance.groupBy')}
            </Typography.Text>
            <Select<ExpenseGrouping>
              value={groupBy}
              onChange={setGroupBy}
              style={{ width: '100%' }}
              options={EXPENSE_GROUPINGS.map((value) => ({
                value,
                label: t(`export.grouping.${value}`),
              }))}
            />
          </Col>
        ) : null}
      </Row>

      <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
        <ServerExportButton
          size="middle"
          title={t('export.menuTitle')}
          run={(format, onProgress) =>
            downloadFinanceExport(kind, format, {
              ...(kind === 'balance-sheet' ? { asOf: to } : { from, to }),
              ...(kind === 'expenses' ? { groupBy } : {}),
              onProgress,
            })
          }
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {/*
            ⚠️ Davr tanlanmaganda oyna YO'Q va serverning o'zi qaror
            qiladi. Buni jimgina qoldirish "hammasi" degan taxminga yo'l
            ochardi — shuning uchun ikki holat ham matn bilan aytiladi.
          */}
          {period
            ? t('export.finance.windowNote', { from, to })
            : t('export.finance.noPeriodNote')}
        </Typography.Text>
      </Space>
    </Card>
  )
}

/**
 * `YYYY-MM-DD` dan bir kun ayiradi.
 *
 * ⚠️ `dayjs` ATAYLAB ishlatilmaydi: bu yerda vaqt zonasi umuman yo'q —
 * qiymat kalendar kuni. `dayjs(iso)` uni BRAUZER zonasidagi yarim
 * tunga bog'lardi va UTC+4:30 dagi operatorda sana bir kunga surilishi
 * mumkin edi. `Date.UTC` esa zonadan mustaqil.
 */
function isoMinusOneDay(iso: string): string {
  const parts = iso.split('-').map(Number)
  const [year, month, day] = parts
  if (year === undefined || month === undefined || day === undefined) return iso
  const shifted = new Date(Date.UTC(year, month - 1, day - 1))
  return shifted.toISOString().slice(0, 10)
}
